import React, { useState, useEffect, useCallback } from 'react';
import {
  Menu,
  MenuItem,
  Typography,
  Divider,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { hu } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';
import notificationService from '../../services/notificationService';
import NotificationBadge from './NotificationBadge'; // Módosított NotificationBadge importálása

const getNotificationUrl = (notification) => {
  let data = notification.data || {};
  // Ha a data string (double-encoded JSON), parse-oljuk
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      data = {};
    }
  }
  switch (notification.type) {
    case 'NEW_BOOKING':
    case 'BOOKING_CONFIRMED':
    case 'BOOKING_CANCELLED':
    case 'BOOKING_REJECTED':
    case 'BOOKING_REMINDER':
    case 'BOOKING_CANCELLED_BY_CLIENT':
    case 'OFFER_RECEIVED':
    case 'OFFER_ACCEPTED':
    case 'OFFER_REJECTED':
      return data.bookingId ? `/foglalasok/${data.bookingId}` : null;
    case 'NEW_REVIEW':
    case 'REVIEW_APPROVED':
    case 'REVIEW_REJECTED':
      if (!data.providerId) return null;
      return data.reviewId
        ? `/szolgaltato/${data.providerId}#review-${data.reviewId}`
        : `/szolgaltato/${data.providerId}`;
    case 'PROVIDER_APPROVED':
    case 'PROVIDER_REJECTED':
      return '/dashboard';
    case 'PAYMENT_INITIATED':
    case 'PAYMENT_CONFIRMED_BY_CLIENT':
    case 'PAYMENT_CONFIRMED_BY_PROVIDER':
    case 'PAYMENT_DEADLINE':
      return data.bookingId ? '/dashboard' : null;
    case 'FORUM_REPLY':
      if (data.threadId && data.replyId) return `/forum/tema/${data.threadId}#reply-${data.replyId}`;
      if (data.threadId) return `/forum/tema/${data.threadId}`;
      return null;
    default:
      return null;
  }
};

const NotificationDropdown = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const open = Boolean(anchorEl);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    fetchRecentNotifications(); // Menü megnyitásakor frissítjük az értesítéseket
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const fetchRecentNotifications = useCallback(async () => {
    if (!user) return;

    setLoadingNotifications(true);
    try {
      // Lekérjük a legutóbbi 5 olvasatlan értesítést
      const response = await notificationService.getUserNotifications(1, 5, true); 
      if (response.notifications) {
        setRecentNotifications(response.notifications);
      }
    } catch (error) {
      console.error('Legutóbbi értesítések lekérése hiba:', error);
      setRecentNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, [user]);

  // Értesítésre kattintás a legördülő menüben
  const handleNotificationClick = async (notification) => {
    try {
      console.log('Notification clicked:', notification);
      console.log('Notification data:', notification.data);
      let data = notification.data || {};
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          data = {};
        }
      }

      let url = getNotificationUrl(notification);
      const state = {};

      // Ha foglalás- vagy fizetésértesítés, a dashboard-ra navigálunk és jelezzük mely ID-t kell megnyitni
      if ([
        'OFFER_RECEIVED', 'OFFER_ACCEPTED', 'OFFER_REJECTED',
        'NEW_BOOKING', 'BOOKING_CONFIRMED', 'BOOKING_CANCELLED', 'BOOKING_REJECTED',
        'BOOKING_REMINDER', 'BOOKING_CANCELLED_BY_CLIENT',
        'PAYMENT_INITIATED', 'PAYMENT_CONFIRMED_BY_CLIENT', 'PAYMENT_CONFIRMED_BY_PROVIDER', 'PAYMENT_DEADLINE',
      ].includes(notification.type)) {
        url = '/dashboard';
        state.scrollToBooking = data.bookingId;
      }

      console.log('Generated URL:', url, 'State:', state);
      await notificationService.markAsRead(notification.id);
      setRecentNotifications(prev => prev.filter(notif => notif.id !== notification.id));
      handleCloseMenu();
      navigate(url || '/ertesitesek', { state });
    } catch (error) {
      console.error('Értesítés olvasottnak jelölése hiba:', error);
      handleCloseMenu();
      navigate('/ertesitesek');
    }
  };

  // Összes értesítés megtekintése gomb
  const handleViewAllNotifications = () => {
    navigate('/ertesitesek');
    handleCloseMenu();
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Tooltip title="Értesítések">
        <IconButton
          color="inherit"
          onClick={handleOpenMenu}
          sx={{ color: 'white' }}
        >
          <NotificationBadge />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
            overflowY: 'auto',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Értesítések</Typography>
          {loadingNotifications && <CircularProgress size={20} />}
        </Box>
        <Divider />
        {recentNotifications.length === 0 && !loadingNotifications ? (
          <MenuItem onClick={handleCloseMenu}>
            <Typography variant="body2" color="text.secondary">Nincsenek új értesítések</Typography>
          </MenuItem>
        ) : loadingNotifications ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          recentNotifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{ whiteSpace: 'normal', py: 1.5 }} // Több soros szöveg kezelése
            >
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {notification.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true, locale: hu })}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={handleViewAllNotifications}>
          <Button fullWidth variant="text" color="primary">
            Összes értesítés megtekintése
          </Button>
        </MenuItem>
      </Menu>
    </>
  );
};

export default NotificationDropdown;